import { Button, Flex } from 'antd'
import { Text } from 'designSystem/components/Text'
import { useEffect, useId, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import styled from 'styled-components'

import { MarkupEditorOverlay } from './MarkupEditorOverlay'
import { VisuallyHiddenLabel } from './markupEditorStyles'
import type {
  MarkupEditorProps,
  MarkupPoint,
  MarkupRectangle,
  MarkupRectangleConstraints,
} from './types'
import { useMarkupEditorInteraction } from './useMarkupEditorInteraction'
import { useMarkupEditorKeyboard } from './useMarkupEditorKeyboard'
import { useMarkupEditorSurface } from './useMarkupEditorSurface'
import { useMarkupEditorViewport } from './useMarkupEditorViewport'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

type EditorError = string | { message: string }

const MIN_ZOOM = 0.5
const MAX_ZOOM = 4
const ZOOM_STEP = 0.25
const HANDLE_RADIUS_PIXELS = 8

const rectangleCoordinates = ({
  x_min,
  y_min,
  x_max,
  y_max,
}: MarkupRectangle) => `${x_min},${y_min},${x_max},${y_max}`

const rectangleConstraintGeometryKey = (
  constraints?: MarkupRectangleConstraints
) => {
  if (!constraints) return 'none'
  const boundary = constraints.boundary
  const obstacles = (constraints.obstacles ?? [])
    .map(rectangleCoordinates)
    .sort()
  return JSON.stringify([
    boundary ? rectangleCoordinates(boundary) : null,
    obstacles,
  ])
}

const points = (vertices: ReadonlyArray<MarkupPoint>) =>
  vertices.map((vertex) => `${vertex.x},${vertex.y}`).join(' ')

const CanvasShell = styled.section`
  display: grid;
  min-width: 0;
  gap: 0.5rem;
`

const Viewport = styled.div`
  width: 100%;
  max-height: 70vh;
  overflow: auto;
`

const PageFrame = styled.div<{ $width: number; $height: number }>`
  position: relative;
  display: inline-block;
  width: ${({ $width }) => `${$width}px`};
  height: ${({ $height }) => `${$height}px`};
  line-height: 0;
  vertical-align: top;
`

const SurfaceImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: fill;
`

const Overlay = styled.svg<{ $ready: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  touch-action: none;
  cursor: crosshair;
  pointer-events: ${({ $ready }) => ($ready ? 'auto' : 'none')};
  outline: none;

  &:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.forecast.primary};
    outline-offset: 0.125rem;
  }
`

const ErrorText = styled(Text)`
  min-height: 1.25rem;
`

export function MarkupEditor(props: MarkupEditorProps) {
  const {
    surface,
    regions,
    annotations,
    selectedOwnerId,
    selectedRegionId,
    selectedAnnotationId,
    drawingIntent,
    capabilities,
    onReadyChange,
  } = props
  const { viewport, zoom, setZoom, renderSettings, display } =
    useMarkupEditorViewport(surface)
  const pageFrameRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const canvasLabelId = useId()
  const viewportLabelId = useId()
  const drawingSurfaceLabelId = useId()
  const [error, setError] = useState<EditorError>()
  const surfaceStatus = useMarkupEditorSurface(
    surface,
    renderSettings,
    setError
  )
  const {
    imageSourceKey,
    sourceKey,
    pageRenderKey,
    ready,
    handleImageLoad,
    handleImageError,
    handlePdfPageLoad,
    handlePdfLoadError,
    handlePdfRenderError,
    handlePdfRenderSuccess,
  } = surfaceStatus
  const handleRadius = renderSettings
    ? HANDLE_RADIUS_PIXELS / renderSettings.pageWidth
    : 0.01
  const handleRadiusY = renderSettings
    ? HANDLE_RADIUS_PIXELS / display.height
    : 0.01
  const selectedRegion = regions.find(
    (region) => region.id === selectedRegionId
  )
  const drawingIntentKey = drawingIntent
    ? [
        drawingIntent.ownerId,
        drawingIntent.regionId,
        drawingIntent.surfaceId,
        drawingIntent.tool,
        drawingIntent.contextKey ?? '',
      ].join(':')
    : 'none'
  const rectangleGeometry = useRef<{
    constraints: MarkupRectangleConstraints | undefined
    signature: string
    revision: number
  }>()
  if (
    !rectangleGeometry.current ||
    rectangleGeometry.current.constraints !== props.rectangleConstraints
  ) {
    const previous = rectangleGeometry.current
    const signature = rectangleConstraintGeometryKey(props.rectangleConstraints)
    rectangleGeometry.current = {
      constraints: props.rectangleConstraints,
      signature,
      revision:
        (previous?.revision ?? 0) +
        Number(previous !== undefined && previous.signature !== signature),
    }
  }
  const rectangleGeometryRevision = rectangleGeometry.current.revision
  const capabilityKey = JSON.stringify({
    tools: [...capabilities.tools].sort(),
    canDraw: capabilities.canDraw,
    canEdit: capabilities.canEdit,
    canDelete: capabilities.canDelete,
    readOnly: capabilities.readOnly,
  })
  const interactionResetKey = JSON.stringify({
    source: sourceKey,
    selectedOwnerId,
    selectedRegionId,
    capabilityKey,
    drawingIntentKey,
    rectangleGeometryRevision,
    minimumRectangleWidth: props.minimumRectangleSize?.width,
    minimumRectangleHeight: props.minimumRectangleSize?.height,
  })
  const interaction = useMarkupEditorInteraction({
    ...props,
    interactionResetKey,
    drawingIntentKey,
    pageRenderKey,
    ready,
    svgRef,
    reportError: setError,
  })
  const { tool, draft } = interaction
  const polygonVertices = draft.kind === 'polygon' ? draft.vertices : []

  const previousSourceKey = useRef(sourceKey)
  useEffect(() => {
    if (previousSourceKey.current === sourceKey) return
    previousSourceKey.current = sourceKey
    setError(undefined)
    if (viewport.current) {
      viewport.current.scrollLeft = 0
      viewport.current.scrollTop = 0
    }
  }, [sourceKey, viewport])

  useEffect(() => {
    onReadyChange?.(ready)
  }, [onReadyChange, ready])

  const { handleKeyDown } = useMarkupEditorKeyboard({
    interaction,
    annotations,
    selectedAnnotationId,
    pageFrameRef,
    svgRef,
    polygonVertexCount: polygonVertices.length,
  })

  const zoomOut = () =>
    setZoom((current) => Math.max(MIN_ZOOM, current - ZOOM_STEP))
  const zoomIn = () =>
    setZoom((current) => Math.min(MAX_ZOOM, current + ZOOM_STEP))
  const fitPage = () => {
    interaction.cancelGesture({ cancelLibraryInteraction: true })
    setZoom(1)
    if (viewport.current) {
      viewport.current.scrollLeft = 0
      viewport.current.scrollTop = 0
    }
  }

  const rectanglePreview =
    draft.kind === 'rectangle'
      ? interaction.rectangleForCreation(draft.start, draft.end)
      : null
  const drawingColor =
    selectedRegion?.color ?? regions[0]?.color ?? 'currentColor'
  const surfacePageNumber =
    surface.kind === 'pdf-page' ? surface.pageNumber : undefined
  const surfaceLabel = surfacePageNumber ? `page ${surfacePageNumber}` : `image`
  const errorMessage = typeof error === 'string' ? error : error?.message
  const errorLanguage = typeof error === 'string' ? 'en' : undefined

  return (
    <CanvasShell aria-labelledby={canvasLabelId}>
      <VisuallyHiddenLabel id={canvasLabelId} lang='en'>
        {`Markup canvas for ${surfaceLabel}`}
      </VisuallyHiddenLabel>
      <Flex
        aria-label={`Markup drawing tools`}
        align='center'
        gap='small'
        lang='en'
        wrap>
        <Button
          aria-pressed={tool === 'select'}
          type={tool === 'select' ? 'primary' : 'default'}
          onClick={() => interaction.selectTool('select')}>
          {`Select`}
        </Button>
        <Button disabled={!ready || zoom <= MIN_ZOOM} onClick={zoomOut}>
          {`Zoom out`}
        </Button>
        <Button disabled={!ready} onClick={fitPage}>
          {`Fit`}
        </Button>
        <Button disabled={!ready || zoom >= MAX_ZOOM} onClick={zoomIn}>
          {`Zoom in`}
        </Button>
        {tool === 'polygon' && polygonVertices.length >= 3 && (
          <Button type='primary' onClick={interaction.completePolygon}>
            {`Complete polygon`}
          </Button>
        )}
        <Text aria-live='polite' color='muted'>
          {renderSettings?.zoomPercent ?? 100}%
        </Text>

        {interaction.drawingIntent && (
          <Button type='text' onClick={() => interaction.cancelDrawing()}>
            {`Cancel drawing`}
          </Button>
        )}
      </Flex>
      {tool === 'polygon' && (
        <Text color='muted' lang='en'>
          {`Click vertices; click the first point, press Enter, or choose Complete polygon.`}
        </Text>
      )}
      <VisuallyHiddenLabel id={viewportLabelId} lang='en'>
        {`Markup page viewport`}
      </VisuallyHiddenLabel>
      <Viewport aria-labelledby={viewportLabelId} ref={viewport}>
        {renderSettings && (
          <PageFrame
            data-markup-page-frame='true'
            ref={pageFrameRef}
            onKeyDown={handleKeyDown}
            onLostPointerCapture={interaction.cancelPointerGesture}
            onPointerCancel={interaction.cancelPointerGesture}
            onPointerMove={(event) =>
              interaction.transitionPointerGesture(event, 'move')
            }
            onPointerUp={(event) =>
              interaction.transitionPointerGesture(event, 'up')
            }
            $height={
              (renderSettings.pageWidth * surface.height) / surface.width
            }
            $width={renderSettings.pageWidth}>
            {surface.kind === 'pdf-page' ? (
              <Document
                error={
                  <span lang='en'>
                    The PDF document could not be loaded. Check the file and try
                    again.
                  </span>
                }
                file={surface.file}
                loading={<span lang='en'>{`Loading PDF document…`}</span>}
                noData={
                  <span lang='en'>No PDF file is available to display.</span>
                }
                onLoadError={() => handlePdfLoadError(true)}>
                <Page
                  key={sourceKey}
                  devicePixelRatio={renderSettings.devicePixelRatio}
                  error={
                    <span lang='en'>
                      The PDF page could not be rendered. Check the file and try
                      again.
                    </span>
                  }
                  loading={<span lang='en'>{`Loading PDF page…`}</span>}
                  noData={
                    <span lang='en'>No PDF page is available to display.</span>
                  }
                  pageNumber={surface.pageNumber}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  rotate={surface.rotationDegrees}
                  width={renderSettings.pageWidth}
                  onLoadSuccess={handlePdfPageLoad}
                  onLoadError={() => handlePdfLoadError()}
                  onRenderError={handlePdfRenderError}
                  onRenderSuccess={handlePdfRenderSuccess}
                />
              </Document>
            ) : (
              <SurfaceImage
                key={imageSourceKey}
                alt={surface.alt ?? `Markup image surface`}
                lang={surface.alt === undefined ? 'en' : undefined}
                src={surface.src}
                onError={handleImageError}
                onLoad={handleImageLoad}
              />
            )}{' '}
            <Overlay
              ref={svgRef}
              $ready={ready}
              aria-labelledby={drawingSurfaceLabelId}
              role='application'
              preserveAspectRatio='none'
              tabIndex={0}
              viewBox='0 0 1 1'
              onPointerDown={interaction.handleSurfacePointerDown}>
              {' '}
              <title id={drawingSurfaceLabelId} lang='en'>
                {`Markup drawing surface on ${surfaceLabel}`}
              </title>
              {rectanglePreview && (
                <rect
                  aria-label={`Proposed annotation rectangle`}
                  lang='en'
                  fill={drawingColor}
                  fillOpacity={0.12}
                  height={rectanglePreview.y_max - rectanglePreview.y_min}
                  role='img'
                  stroke={drawingColor}
                  strokeDasharray='1 5'
                  strokeLinecap='round'
                  strokeWidth={2}
                  vectorEffect='non-scaling-stroke'
                  width={rectanglePreview.x_max - rectanglePreview.x_min}
                  x={rectanglePreview.x_min}
                  y={rectanglePreview.y_min}
                />
              )}
              {polygonVertices.length > 0 && (
                <>
                  <polyline
                    fill='none'
                    points={points(polygonVertices)}
                    stroke={drawingColor}
                    strokeDasharray='1 5'
                    strokeLinecap='round'
                    strokeWidth={2}
                    vectorEffect='non-scaling-stroke'
                  />
                  {polygonVertices.map((vertex, index) => {
                    const vertexNumber = index + 1
                    return (
                      <ellipse
                        aria-label={
                          index === 0 && polygonVertices.length >= 3
                            ? `Close polygon at first vertex`
                            : `Polygon vertex ${vertexNumber}`
                        }
                        lang='en'
                        data-polygon-first={index === 0 ? 'true' : undefined}
                        key={`${vertex.x}-${vertex.y}-${index}`}
                        cx={vertex.x}
                        cy={vertex.y}
                        fill={drawingColor}
                        rx={handleRadius}
                        ry={handleRadiusY}
                        stroke={drawingColor}
                        strokeWidth={2}
                        vectorEffect='non-scaling-stroke'
                        onPointerDown={(event) => {
                          event.stopPropagation()
                          if (index === 0 && polygonVertices.length >= 3)
                            interaction.completePolygon()
                        }}
                      />
                    )
                  })}
                </>
              )}
            </Overlay>
            <MarkupEditorOverlay
              selectedAnnotationId={selectedAnnotationId}
              annotations={annotations.map((annotation) => ({
                ...annotation,
                geometry:
                  annotation.id === selectedAnnotationId &&
                  draft.kind === 'vertex'
                    ? draft.preview
                    : annotation.geometry,
              }))}
              display={display}
              interaction={interaction}
              getPageBounds={() =>
                pageFrameRef.current?.getBoundingClientRect()
              }
              regions={regions}
            />
          </PageFrame>
        )}
      </Viewport>
      {!renderSettings && (
        <Text color='muted' lang='en'>{`Preparing markup canvas…`}</Text>
      )}
      <ErrorText color='destructive' lang={errorLanguage} role='status'>
        {errorMessage}
      </ErrorText>
    </CanvasShell>
  )
}
